import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  TransformComponent,
  TransformWrapper,
  type ReactZoomPanPinchRef,
} from "react-zoom-pan-pinch";
import { FitIcon, ZoomInIcon, ZoomOutIcon } from "./icons.js";

/**
 * The center-pane image viewer. Wraps the image in `react-zoom-pan-pinch`
 * (MIT) so a support engineer can wheel-zoom, drag-pan, and pinch into a panel
 * photo or read image to read a faint breaker stamp.
 *
 * First-display fit: when a photo is shown the image is zoomed to **fit the
 * viewer, or 100% (1 image-pixel : 1 screen-pixel), whichever is smaller** —
 * big panel shots shrink to fit; an already-small crop is never magnified past
 * its real resolution. We mount at 1× and snap to the computed fit on load
 * (revealing only once fitted, so there is no first-frame jump).
 *
 * `overlay` renders inside the transform at the image's natural pixel size, so
 * percentage-positioned children (e.g. the geometry/column boxes) track the
 * image as it zooms and pans.
 */
export type ImageViewerProps = {
  readonly src: string;
  readonly alt: string;
  readonly overlay?: ReactNode;
};

const MIN_SCALE = 0.1;
const MAX_SCALE = 8;

/**
 * First-display scale: fit the image into the viewer, but never magnify past
 * 100% (1 image-pixel : 1 screen-pixel) — whichever is smaller. Returns 1 (a
 * safe no-op) when either box hasn't been measured yet.
 */
export function fitScale(
  natural: { readonly w: number; readonly h: number },
  container: { readonly w: number; readonly h: number },
): number {
  if (natural.w <= 0 || natural.h <= 0 || container.w <= 0 || container.h <= 0) {
    return 1;
  }
  return Math.min(1, container.w / natural.w, container.h / natural.h);
}

/**
 * Slider position (0..1) ⇄ scale, on a log axis so each slider pixel is a
 * constant *ratio* of zoom — a perceptually even bar, instead of a linear one
 * where the whole low end is crammed into the first sliver. Clamped to the
 * scale bounds so a stray value can never push the thumb off the track.
 */
export function scaleToSlider(scale: number): number {
  const clamped = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
  return Math.log(clamped / MIN_SCALE) / Math.log(MAX_SCALE / MIN_SCALE);
}

export function sliderToScale(position: number): number {
  const clamped = Math.min(1, Math.max(0, position));
  return MIN_SCALE * (MAX_SCALE / MIN_SCALE) ** clamped;
}

/** A zoom scale as a whole-percent label, e.g. 1.42 → "142%". */
export function formatZoom(scale: number): string {
  return `${Math.round(scale * 100)}%`;
}

export function ImageViewer({ src, alt, overlay }: ImageViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const apiRef = useRef<ReactZoomPanPinchRef>(null);
  // Natural pixel size of the loaded image, sized once it's known so the
  // overlay box can be percentage-positioned against it.
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  // Stay invisible until the first fit is applied, so a large image doesn't
  // flash at 1× before snapping to fit.
  const [fitted, setFitted] = useState(false);
  // Live zoom scale, mirrored from the library on every transform so the
  // percentage readout and slider thumb track wheel/pinch/drag-zoom too.
  const [scale, setScale] = useState(1);

  // Fit-to-viewer but never past 100%: min(viewer/image ratios, capped at 1).
  const applyFit = useCallback((animate = false) => {
    const img = imgRef.current;
    const container = containerRef.current;
    const api = apiRef.current;
    if (img === null || container === null || api === null) return;
    const natural = { w: img.naturalWidth, h: img.naturalHeight };
    const box = { w: container.clientWidth, h: container.clientHeight };
    if (natural.w === 0 || natural.h === 0 || box.w === 0 || box.h === 0) return;
    api.centerView(fitScale(natural, box), animate ? 200 : 0);
    setFitted(true);
  }, []);

  // Jump to an exact scale (slider drag, 100% button), centred in the viewer.
  const zoomTo = useCallback((next: number, animate = false) => {
    apiRef.current?.centerView(next, animate ? 200 : 0);
  }, []);

  const onLoad = useCallback(() => {
    const img = imgRef.current;
    if (img !== null) {
      setNatural({ w: img.naturalWidth, h: img.naturalHeight });
    }
    applyFit(false);
  }, [applyFit]);

  // A new photo: hide and re-fit. Covers the cached-image case too, where the
  // load event can fire before React attaches the handler (img already
  // `complete`), so we fit straight away instead of waiting for `onLoad`.
  // `src` is the intended trigger — it isn't read in the body, so Biome counts
  // it as extra, but re-running on a photo change is exactly the point.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-fit must run when src changes
  useEffect(() => {
    setFitted(false);
    setNatural(null);
    const img = imgRef.current;
    if (img !== null && img.complete && img.naturalWidth > 0) onLoad();
  }, [src, onLoad]);

  return (
    <div
      ref={containerRef}
      className="relative min-h-0 min-w-0 flex-1 overflow-hidden rounded-lg border border-line-strong bg-bg-0"
    >
      <TransformWrapper
        ref={apiRef}
        initialScale={1}
        minScale={MIN_SCALE}
        maxScale={MAX_SCALE}
        // `smooth: false` is the load-bearing fix: with smoothing on, the
        // library multiplies `step` by the raw `event.deltaY`, so a hi-res
        // mouse wheel or trackpad (deltaY of 1 vs 120) zooms by wildly
        // different, unpredictable amounts. Off, every notch is a constant,
        // predictable step.
        smooth={false}
        wheel={{ step: 0.15 }}
        doubleClick={{ mode: "zoomIn", step: 0.5 }}
        onTransform={(_ref, state) => setScale(state.scale)}
      >
        <TransformComponent
          wrapperStyle={{ width: "100%", height: "100%" }}
          wrapperClass="!cursor-grab active:!cursor-grabbing"
        >
          <div
            className="relative"
            style={
              natural !== null
                ? { width: natural.w, height: natural.h }
                : undefined
            }
          >
            <img
              ref={imgRef}
              src={src}
              alt={alt}
              onLoad={onLoad}
              draggable={false}
              className={`block max-w-none select-none transition-opacity duration-150 ${
                fitted ? "opacity-100" : "opacity-0"
              }`}
            />
            {overlay}
          </div>
        </TransformComponent>
      </TransformWrapper>

      <div className="absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1 rounded-lg border border-border-1 bg-bg-2/90 p-1 shadow-sm backdrop-blur">
        <ViewerButton label="Zoom out" onClick={() => apiRef.current?.zoomOut()}>
          <ZoomOutIcon />
        </ViewerButton>
        <input
          type="range"
          aria-label="Zoom level"
          min={0}
          max={1}
          step={0.001}
          value={scaleToSlider(scale)}
          onChange={(e) => zoomTo(sliderToScale(e.target.valueAsNumber))}
          style={{ accentColor: "var(--accent)" }}
          className="h-1.5 w-32 cursor-pointer appearance-none rounded-full bg-bg-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        />
        <ViewerButton label="Zoom in" onClick={() => apiRef.current?.zoomIn()}>
          <ZoomInIcon />
        </ViewerButton>
        <button
          type="button"
          aria-label="Reset zoom to 100%"
          title="Reset zoom to 100%"
          onClick={() => zoomTo(1, true)}
          className="w-12 rounded-md border border-transparent px-1 py-1 text-center font-mono text-fg-2 text-xs tabular-nums transition-colors hover:border-border-1 hover:bg-bg-4 hover:text-fg-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          {formatZoom(scale)}
        </button>
        <ViewerButton label="Fit to viewer" onClick={() => applyFit(true)}>
          <FitIcon />
        </ViewerButton>
      </div>
    </div>
  );
}

function ViewerButton({
  label,
  onClick,
  children,
}: {
  readonly label: string;
  readonly onClick: () => void;
  readonly children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="flex h-7 w-7 items-center justify-center rounded-md border border-transparent text-fg-2 transition-colors hover:border-border-1 hover:bg-bg-4 hover:text-fg-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      {children}
    </button>
  );
}
